// /demo — single-page render of every shared component for design QA.
// Visit https://tcs-erp-fd3ae.web.app/demo (after deploy) or localhost:5173/demo
// during dev. NOT linked from the nav — only reachable by direct URL.
//
// Purpose: lets Ben sign off on the design system before module retrofits
// touch real screens. If something here looks wrong, fix the component;
// don't fix it in the consumer screen.

import {
  Bell,
  Building2,
  CheckCircle2,
  ChevronRight,
  MessageSquare,
  Package,
  Plus,
  Search,
  Sparkles,
} from 'lucide-react';
import { useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  CategoryPill,
  EmptyState,
  ErrorState,
  FacilityCard,
  IconButton,
  MarketStripe,
  MetaLabel,
  PageHeader,
  RagDot,
  Skeleton,
  StatusPill,
  TabBar,
  UrgencyBadge,
} from '../../shared/components';
import { useMarkets, marketColorPair } from '../../shared/markets';

export default function Demo() {
  const [tab, setTab] = useState<'activity' | 'tasks' | 'status' | 'photos'>(
    'activity',
  );
  const {
    markets,
    loading: marketsLoading,
    error: marketsError,
  } = useMarkets();

  return (
    <div className="min-h-full bg-tcs-bg-muted">
      <div className="mx-auto max-w-6xl space-y-12 p-8">
        <PageHeader
          eyebrow="Internal · v0.1 · 2026-05-09"
          title="Design system demo"
          meta="Every shared component on one page. Not linked from nav — direct URL only."
          actions={
            <>
              <Button variant="secondary" size="sm" icon={<Search className="h-4 w-4" />}>
                Find
              </Button>
              <Button size="sm" icon={<Plus className="h-4 w-4" />}>
                New
              </Button>
            </>
          }
        />

        {/* ---------- 01 · Color ---------- */}
        <Section eyebrow="01" title="Color">
          <Block label="Neutrals">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
              <Swatch hex="#FFFFFF" name="bg" note="white" border />
              <Swatch hex="#FAFAF9" name="bg-muted" note="stone-50" />
              <Swatch hex="#F5F5F4" name="surface" note="stone-100" />
              <Swatch hex="#E7E5E4" name="border" note="stone-200" />
              <Swatch hex="#475569" name="text-2°" note="slate-600" dark />
              <Swatch hex="#0F172A" name="text" note="slate-900" dark />
            </div>
          </Block>

          <Block label="RAG · facility health (filled dot)">
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <RagDot status="Green" size="lg" />
                <span className="text-sm">Green · Healthy</span>
              </div>
              <div className="flex items-center gap-2">
                <RagDot status="Amber" size="lg" />
                <span className="text-sm">Amber · Needs attention</span>
              </div>
              <div className="flex items-center gap-2">
                <RagDot status="Red" size="lg" />
                <span className="text-sm">Red · At risk</span>
              </div>
              <div className="flex items-center gap-2">
                <RagDot status={null} size="lg" />
                <span className="text-sm">No data</span>
              </div>
            </div>
          </Block>

          <Block label="Sentiment pills">
            <div className="flex flex-wrap gap-3">
              <StatusPill variant="sentiment" value="Positive" />
              <StatusPill variant="sentiment" value="Neutral" />
              <StatusPill variant="sentiment" value="Negative" />
              <StatusPill variant="sentiment" value="Critical" />
            </div>
          </Block>

          <Block label="Lifecycle pills">
            <div className="flex flex-wrap gap-3">
              <StatusPill variant="lifecycle" value="Active" />
              <StatusPill variant="lifecycle" value="Pending" />
              <StatusPill variant="lifecycle" value="Closed" />
              <StatusPill variant="lifecycle" value="FLAG" />
            </div>
          </Block>

          <Block label="Urgency · supply tier">
            <div className="flex flex-wrap gap-3">
              <UrgencyBadge tier="Emergency" withTimeline />
              <UrgencyBadge tier="Soon" withTimeline />
              <UrgencyBadge tier="Planned" withTimeline />
            </div>
          </Block>

          <Block label="Supply categories">
            <div className="flex flex-wrap gap-3">
              <CategoryPill category="Chemical" />
              <CategoryPill category="Paper" />
              <CategoryPill category="Equipment" />
              <CategoryPill category="Consumable" />
              <CategoryPill category="Other" />
            </div>
          </Block>

          <Block label="PO / order status">
            <div className="flex flex-wrap gap-3">
              <StatusPill variant="po" value="Draft" />
              <StatusPill variant="po" value="Sent" />
              <StatusPill variant="po" value="Shipped" />
              <StatusPill variant="po" value="Delivered" />
              <StatusPill variant="po" value="Invoiced" />
              <StatusPill variant="po" value="Cancelled" />
            </div>
          </Block>

          <Block label="Vendor coverage">
            <div className="flex flex-wrap gap-3">
              <StatusPill variant="coverage" value="Local" />
              <StatusPill variant="coverage" value="Regional" />
              <StatusPill variant="coverage" value="National" />
            </div>
          </Block>

          {/* The swatches are READ from public.markets, so this page shows
              what the business actually runs rather than a copy of it that
              drifts. A market added by a row appears here with no deploy. */}
          <Block label="Markets · stripe accents (read from the markets table)">
            {marketsLoading && (
              <div className="text-sm text-tcs-text-muted">Loading markets…</div>
            )}
            {marketsError && (
              <div className="text-sm text-tcs-text-muted">
                The market list could not be loaded, so every stripe on this page
                renders neutral. That is the honest answer, not a fallback copy.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {markets.map((m) => (
                <Card key={m.market_name} className="flex items-center gap-3 p-3">
                  <MarketStripe region={m.market_name} width={6} className="h-7" />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {m.market_name}
                    </div>
                    <div className="text-[11px] text-tcs-text-muted">
                      {m.region_name} · {m.covers_states.join(', ')}
                    </div>
                    <div className="font-mono text-[11px] text-tcs-text-muted">
                      {marketColorPair(markets, m.market_name).base}
                      {m.dot_color_dark
                        ? ` · dark ${m.dot_color_dark}`
                        : ''}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Block>
        </Section>

        {/* ---------- 02 · Type ---------- */}
        <Section eyebrow="02" title="Typography">
          <Card className="space-y-3 p-6">
            <MetaLabel>DM Sans · UI &amp; body</MetaLabel>
            <div className="text-4xl font-bold tracking-tight">$1.24M ARR</div>
            <div className="text-3xl font-semibold tracking-tight">
              Account Management
            </div>
            <div className="text-2xl font-semibold tracking-tight">
              My Accounts
            </div>
            <div className="text-xl font-semibold">
              Surgery Center of the Rockies
            </div>
            <div className="text-base text-tcs-text">
              Night crew replaced supplies in rooms 3 and 7 per Tuesday request.
            </div>
            <div className="text-sm text-tcs-text-secondary">
              Same line at body size · slightly muted ink.
            </div>
            <div className="text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
              Updated 12 min ago · Kim A.
            </div>
          </Card>
          <Card className="grid grid-cols-1 gap-6 p-6 md:grid-cols-3">
            <div>
              <MetaLabel>Facility ID</MetaLabel>
              <div className="mt-1 font-mono text-lg">FAC-0160</div>
            </div>
            <div>
              <MetaLabel>PO number</MetaLabel>
              <div className="mt-1 font-mono text-lg">PO-0023</div>
            </div>
            <div>
              <MetaLabel>Revenue</MetaLabel>
              <div className="mt-1 font-mono text-lg">$12,480.00</div>
            </div>
          </Card>
        </Section>

        {/* ---------- 03 · Cards & rows ---------- */}
        <Section eyebrow="03" title="Cards and facility rows">
          <Block label="Facility card · what AM list will look like">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <FacilityCard
                facilityName="Rocky Mountain Surgery Center"
                facilityId="FAC-0160"
                facilityType="Surgery_Center"
                addressSummary="Denver, CO"
                rag="Red"
                sentiment="Critical"
                region="Denver Metro"
                href="#"
                meta={<span>3 open tasks · last walk 4d ago</span>}
              />
              <FacilityCard
                facilityName="Heartland Dialysis — Chesterfield"
                facilityId="FAC-0083"
                facilityType="Dialysis"
                addressSummary="Chesterfield, MO"
                rag="Amber"
                sentiment="Neutral"
                region="St. Louis Metro"
                href="#"
                meta={<span>1 open task · last walk Apr 21</span>}
              />
              <FacilityCard
                facilityName="Foothills MOB — Lakewood"
                facilityId="FAC-0098"
                facilityType="MOB"
                addressSummary="Lakewood, CO"
                rag="Green"
                sentiment="Positive"
                region="Denver Metro"
                href="#"
                meta={<span>0 open tasks · walked today</span>}
              />
              <FacilityCard
                facilityName="Overland MOB — Leawood"
                facilityId="FAC-0142"
                facilityType="MOB"
                addressSummary="Leawood, KS"
                rag={null}
                lifecycle="Pending"
                region="Kansas City Metro"
                href="#"
                meta={<span>Onboarding · pending start date</span>}
              />
            </div>
          </Block>

          <Block label="Activity feed row · facility detail">
            <Card className="divide-y divide-tcs-border">
              <ActivityRow
                icon={<MessageSquare className="h-4 w-4" />}
                tone="amber"
                title="Feedback"
                pill={<StatusPill variant="sentiment" value="Negative" />}
                meta="· Kim A. · 2h ago"
                body="OR 3 trash not emptied during Tuesday close. Spoke with night supervisor."
              />
              <ActivityRow
                icon={<Package className="h-4 w-4" />}
                tone="blue"
                title="Supply request"
                pill={
                  <>
                    <CategoryPill category="Chemical" />
                    <UrgencyBadge tier="Soon" />
                  </>
                }
                meta="· Marco R. · 5h ago"
                body="2 gal Virex II 256 — running low Friday."
              />
              <ActivityRow
                icon={<CheckCircle2 className="h-4 w-4" />}
                tone="emerald"
                title="Walkthrough complete"
                pill={<StatusPill variant="sentiment" value="Positive" />}
                meta="· Ben M. · Yesterday 6:40pm"
                body="Score 94. All ATP within threshold. Uploaded 12 photos."
              />
            </Card>
          </Block>
        </Section>

        {/* ---------- 04 · Buttons ---------- */}
        <Section eyebrow="04" title="Buttons">
          <Card className="space-y-4 p-6">
            <div className="flex flex-wrap items-center gap-3">
              <Button>Primary action</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="destructive">Destructive</Button>
              <Button variant="ghost">Ghost</Button>
              <IconButton label="More options" variant="bordered">
                <Bell className="h-4 w-4" />
              </IconButton>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button size="sm">Small · primary</Button>
              <Button variant="secondary" size="sm">
                Small · secondary
              </Button>
              <Button loading>Submitting…</Button>
              <Button icon={<Sparkles className="h-4 w-4" />}>
                Voice input
              </Button>
            </div>
          </Card>
        </Section>

        {/* ---------- 05 · Tabs ---------- */}
        <Section eyebrow="05" title="Navigation">
          <Block label="Tab bar · facility detail">
            <Card className="p-2">
              <TabBar
                activeId={tab}
                onChange={setTab}
                tabs={[
                  { id: 'activity', label: 'Activity Feed' },
                  { id: 'tasks', label: 'Tasks', count: 3 },
                  { id: 'status', label: 'Status' },
                  { id: 'photos', label: 'Photos' },
                ]}
              />
              <div className="px-3 py-4 text-sm text-tcs-text-secondary">
                Active tab: <span className="font-mono">{tab}</span>
              </div>
            </Card>
          </Block>
          <Block label="Avatars">
            <div className="flex items-center gap-3">
              <Avatar name="Ben Meier" size="sm" />
              <Avatar name="Ben Meier" size="md" />
              <Avatar name="Ben Meier" size="lg" />
              <Avatar name="Antonela Chesini" tone="muted" />
              <Avatar initials="BS" />
            </div>
          </Block>
        </Section>

        {/* ---------- 06 · States ---------- */}
        <Section eyebrow="06" title="Empty / Loading / Error">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <EmptyState
              title="No approvals pending"
              body="New supply requests will appear here."
              cta={<Button size="sm">View all orders</Button>}
            />
            <Card className="space-y-3 p-5">
              <MetaLabel>Loading skeleton</MetaLabel>
              <Skeleton block height={12} width="33%" />
              <Skeleton block height={20} width="83%" />
              <Skeleton block height={12} width="66%" />
              <Skeleton block height={64} width="100%" />
              <div className="flex gap-2">
                <Skeleton height={24} width={80} />
                <Skeleton height={24} width={64} />
              </div>
            </Card>
            <ErrorState
              body="Network unavailable. We'll retry automatically."
              onRetry={() => alert('Retry clicked')}
            />
          </div>
        </Section>

        {/* ---------- 07 · Iconography ---------- */}
        <Section eyebrow="07" title="Iconography · Lucide">
          <Card className="grid grid-cols-4 gap-4 p-6 md:grid-cols-8">
            <IconCell name="bell" Icon={Bell} />
            <IconCell name="search" Icon={Search} />
            <IconCell name="plus" Icon={Plus} />
            <IconCell name="building-2" Icon={Building2} />
            <IconCell name="package" Icon={Package} />
            <IconCell name="check-circle-2" Icon={CheckCircle2} />
            <IconCell name="message-square" Icon={MessageSquare} />
            <IconCell name="chevron-right" Icon={ChevronRight} />
          </Card>
        </Section>
      </div>
    </div>
  );
}

// ----- Local helpers (only used by this page) ------------------------------

function Section({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="flex items-baseline gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-tcs-text-muted">
          {eyebrow}
        </span>
        <h2 className="text-[28px] font-semibold tracking-tight">{title}</h2>
      </div>
      <div className="space-y-6">{children}</div>
    </section>
  );
}

function Block({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <MetaLabel>{label}</MetaLabel>
      {children}
    </div>
  );
}

function Swatch({
  hex,
  name,
  note,
  border,
  dark,
}: {
  hex: string;
  name: string;
  note: string;
  border?: boolean;
  dark?: boolean;
}) {
  return (
    <div>
      <div
        className={`aspect-[1.6/1] rounded-lg ${border ? 'border border-tcs-border' : ''} ${dark ? 'border border-black/10' : ''}`}
        style={{ backgroundColor: hex }}
      />
      <div className="mt-2 text-xs font-medium">{name}</div>
      <div className="font-mono text-[11px] text-tcs-text-muted">
        {hex} · {note}
      </div>
    </div>
  );
}

type ActivityTone = 'amber' | 'blue' | 'emerald';

function ActivityRow({
  icon,
  tone,
  title,
  pill,
  meta,
  body,
}: {
  icon: React.ReactNode;
  tone: ActivityTone;
  title: string;
  pill?: React.ReactNode;
  meta?: string;
  body: string;
}) {
  const toneClasses: Record<ActivityTone, string> = {
    amber: 'bg-st-warn-bg-strong text-st-warn-fg',
    blue: 'bg-st-info-bg-strong text-st-info-fg',
    emerald: 'bg-st-ok-bg-strong text-st-ok-fg',
  };
  return (
    <div className="flex items-start gap-3 p-4">
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${toneClasses[tone]}`}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">{title}</span>
          {pill}
          {meta && <span className="text-xs text-tcs-text-muted">{meta}</span>}
        </div>
        <div className="mt-1 text-sm text-tcs-text-secondary">{body}</div>
      </div>
    </div>
  );
}

function IconCell({
  name,
  Icon,
}: {
  name: string;
  Icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex flex-col items-center gap-1 text-tcs-text-secondary">
      <Icon className="h-5 w-5" />
      <span className="font-mono text-[10px] text-tcs-text-muted">{name}</span>
    </div>
  );
}
